import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { UserFeedFollowGroupFragment } from 'api';
import { GroupAvatar, MobileSubmenu, UserAvatar } from 'components';
import { generateLink, Groups, Index } from 'Links';
import { useCurrentUser } from 'hooks';

const styles = {
  list: {
    width: '100%',
  },
  listItem: {
    paddingTop: 12,
    paddingBottom: 12,
  },
  selected: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  selectedIcon: {
    marginRight: 16,
  },
  addIcon: {
    fontSize: 20,
  },
} as const;

const ICON_SIZE = 20;

interface Props {
  groups: UserFeedFollowGroupFragment[];
  isHomeSelected: () => boolean;
  isGroupSelected: (group: UserFeedFollowGroupFragment) => boolean;
}

const SelectMobile: FC<Props> = props => {
  const { isHomeSelected, isGroupSelected, groups } = props;

  const currentUser = useCurrentUser();
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  function toggleExpanded(isExpanded: boolean) {
    setExpanded(isExpanded);
  }

  function renderMobileSelected() {
    const selectedGroup = groups.find(isGroupSelected);

    if (isHomeSelected() || !selectedGroup) {
      return (
        <Typography variant="subtitle2" sx={styles.selected}>
          <UserAvatar size={ICON_SIZE} user={currentUser} sx={styles.selectedIcon} />
          {t('components:timeline')}
        </Typography>
      );
    }

    return (
      <Typography variant="subtitle2" sx={styles.selected}>
        <GroupAvatar size={ICON_SIZE} group={selectedGroup} sx={styles.selectedIcon} />
        {selectedGroup.title}
      </Typography>
    );
  }

  function renderMobileHome() {
    const itemProps = {
      selected: isHomeSelected(),
      component: Index,
      sx: styles.listItem,
    };

    return (
      <ListItem button {...itemProps}>
        <ListItemIcon>
          <UserAvatar size={ICON_SIZE} user={currentUser} />
        </ListItemIcon>
        <ListItemText disableTypography>
          <Typography>{t('components:timeline')}</Typography>
        </ListItemText>
      </ListItem>
    );
  }

  function renderMobileGroupItem(group: UserFeedFollowGroupFragment) {
    const isSelected = isGroupSelected(group);

    const itemProps = {
      key: group.id,
      selected: isSelected,
      component: generateLink(`/group/${group.id}`),
      sx: styles.listItem,
    };

    return (
      <ListItemButton {...itemProps}>
        <ListItemIcon>
          <GroupAvatar size={ICON_SIZE} group={group} />
        </ListItemIcon>
        <ListItemText disableTypography>
          <Typography>{group.title}</Typography>
        </ListItemText>
      </ListItemButton>
    );
  }

  function renderMobileDiscover() {
    const itemProps = {
      component: Groups,
      sx: styles.listItem,
    };

    return (
      <ListItemButton {...itemProps}>
        <ListItemIcon>
          <AddIcon sx={styles.addIcon} />
        </ListItemIcon>
        <ListItemText disableTypography>
          <Typography>{t('feed:discoverGroups')}</Typography>
        </ListItemText>
      </ListItemButton>
    );
  }

  return (
    <MobileSubmenu expanded={expanded} selected={renderMobileSelected()} onToggle={toggleExpanded}>
      <List component="nav" sx={styles.list}>
        {renderMobileHome()}
        {groups.map(renderMobileGroupItem)}
        {renderMobileDiscover()}
      </List>
    </MobileSubmenu>
  );
};

export default SelectMobile;
